package com.rosario.juridico.premium

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.rosario.juridico.domain.AppMontos

/**
 * Origen de la compra para trazabilidad básica.
 */
enum class PurchaseSource {
    WEB_MERCADOPAGO,
    ANDROID_PLAY_STORE,
    UNKNOWN
}

/**
 * Planes disponibles (Placeholder para futura expansión).
 */
enum class PremiumPlan(val id: String, val precio: Double) {
    MENSUAL("premium_mensual", AppMontos.SUBSCRIPCION_MENSUAL_MP),
    ANUAL("premium_anual", AppMontos.SUBSCRIPCION_ANUAL_MP),
    FREE("free", 0.0)
}

/**
 * Estados del flujo de compra para UX.
 */
sealed class PurchaseState {
    object Idle : PurchaseState()
    object Processing : PurchaseState()
    object Restoring : PurchaseState()
    object Success : PurchaseState()
    data class Error(val message: String) : PurchaseState()
}

/**
 * Gestor centralizado para el flujo de compra de suscripciones.
 * Desacopla la UI de los proveedores de pago (MercadoPago, Play Billing).
 */
object PremiumPurchaseManager {
    private var provider: PurchaseProvider? = null

    /**
     * Inicializa el manager con el proveedor específico de la plataforma.
     */
    fun initialize(provider: PurchaseProvider) {
        this.provider = provider
    }

    // --- Estado de la Operación (Reactivo para UI) ---
    
    var purchaseState by mutableStateOf<PurchaseState>(PurchaseState.Idle)
        private set

    var lastPurchaseSource by mutableStateOf(PurchaseSource.UNKNOWN)
        private set

    /**
     * Inicia el proceso de compra para un plan específico.
     * Protección anti race conditions: solo una operación a la vez.
     */
    fun startPremiumPurchase(plan: PremiumPlan, source: PurchaseSource) {
        if (purchaseState is PurchaseState.Processing || purchaseState is PurchaseState.Restoring) return
        
        purchaseState = PurchaseState.Processing
        lastPurchaseSource = source
        PremiumTelemetry.logEvent("premium_purchase_started", mapOf("plan" to plan.id, "source" to source.name))

        try {
            provider?.startPurchase(plan)
        } catch (e: Exception) {
            purchaseState = PurchaseState.Error(e.message ?: "Error desconocido")
            PremiumTelemetry.logEvent("premium_purchase_failed_init", mapOf("error" to (e.message ?: "unknown")))
        }
    }

    /**
     * Notifica éxito en la compra. Actualiza el estado y refresca el perfil.
     */
    suspend fun notifyPurchaseSuccess(uid: String) {
        purchaseState = PurchaseState.Success
        PremiumTelemetry.logEvent("premium_purchase_success", mapOf("uid" to uid))
        // Sincronizar estado premium global inmediatamente
        PremiumManager.loadUserProfile(uid, forceRefresh = true)
    }

    /**
     * Permite forzar el estado de procesamiento externamente (ej: al volver de Mercado Pago)
     */
    fun setProcessingState() {
        purchaseState = PurchaseState.Processing
    }

    /**
     * Notifica error en la compra.
     */
    fun notifyPurchaseError(message: String) {
        purchaseState = PurchaseState.Error(message)
        PremiumTelemetry.logEvent("premium_purchase_error", mapOf("message" to message))
    }

    /**
     * Resetea el estado a Idle.
     */
    fun resetState() {
        purchaseState = PurchaseState.Idle
    }

    /**
     * Notifica al manager que el flujo externo ha terminado (éxito o cancelación).
     * Esto debe ser llamado por los adapters si es necesario resetear el estado.
     */
    fun endPurchaseFlow() {
        if (purchaseState is PurchaseState.Processing || purchaseState is PurchaseState.Restoring) {
            purchaseState = PurchaseState.Idle
            PremiumTelemetry.logEvent("premium_purchase_flow_ended")
        }
    }

    /**
     * Cancela el flujo de compra actual.
     */
    fun cancelPurchaseFlow() {
        provider?.cancelPurchase()
        purchaseState = PurchaseState.Idle
        PremiumTelemetry.logEvent("premium_purchase_cancelled")
    }

    /**
     * Intenta restaurar compras previas con protección anti race conditions.
     */
    fun restorePurchases() {
        if (purchaseState is PurchaseState.Processing || purchaseState is PurchaseState.Restoring) return
        
        purchaseState = PurchaseState.Restoring
        PremiumTelemetry.logEvent("premium_restore_started")
        
        try {
            provider?.restorePurchases()
        } catch (e: Exception) {
            purchaseState = PurchaseState.Error("Error al restaurar: ${e.message}")
            PremiumTelemetry.logRestoreFailed("unknown", e.message ?: "unknown")
        }
    }

    /**
     * Notifica éxito en la restauración.
     */
    suspend fun notifyRestoreSuccess(uid: String) {
        PremiumManager.restoreEntitlements(uid)
        purchaseState = PurchaseState.Success
        PremiumTelemetry.logRestoreSuccess(uid)
    }
}
